import { useCallback, useEffect, useState } from 'react';
import { resolveApiUrl } from '../utils/subdomainUtils';

export interface UseCSRFTokenResult {
  token: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Best-effort CSRF prefetch for session-backed mutating requests.
 *
 * GET /api/auth/csrf-token returns 401 when there is no `sid` cookie.
 * CSRF middleware also skips validation when there is no `sid`, so a missing
 * token on cold start is expected and must not block login.
 *
 * @see GET /api/auth/csrf-token
 */
export function useCSRFToken(): UseCSRFTokenResult {
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8_000);
    try {
      const res = await fetch(resolveApiUrl('/api/auth/csrf-token'), {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      const body = (await res.json().catch(() => null)) as
        | { success?: boolean; token?: string; error?: string }
        | null;

      // No session yet — CSRF not required for first login. Not an error.
      if (res.status === 401) {
        setToken(null);
        setError(null);
        return;
      }

      if (!res.ok || !body?.success || !body.token) {
        setToken(null);
        // Soft failure: authFetch will attach CSRF when a session exists.
        setError(null);
        return;
      }
      setToken(body.token);
    } catch {
      setToken(null);
      setError(null);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { token, loading, error, refresh };
}
